import { extendTailwindMerge } from "tailwind-merge";

/*
 * cn joins class names and resolves Tailwind conflicts, so the last class named
 * for a property wins regardless of where it sits in the compiled stylesheet.
 * That is what makes `className` a real override: a caller's `rounded-full`
 * beats a component's baked-in `rounded-paper` because it comes later in the
 * call, not because of CSS source order. Without it the only lever a caller had
 * was the `!` important modifier.
 *
 * tailwind-merge only knows the stock utility scales, and the kit's vocabulary
 * is almost entirely custom (tokens.css + the @theme block and @utility rules in
 * utilities.css). Every entry below teaches it one of those, and each is here
 * for one of two reasons: a custom utility that must merge with its stock
 * siblings, or a custom utility that must NOT be mistaken for one.
 *
 * Colors need no entry: tailwind-merge's `color` theme scale accepts any name,
 * so bg-paper/bg-desk, text-ink/text-annotation and border-rule already merge
 * per property out of the box.
 */
export const cn = extendTailwindMerge<"text-annotation-caps">({
  extend: {
    theme: {
      /*
       * --radius-* (paper, sheet, tag) plus the @utility rounded-swipe. All four
       * set border-radius, so they merge against rounded-full/rounded-md and
       * each other. Unlisted they match no group at all and simply pile up,
       * leaving the winner to stylesheet order.
       */
      radius: ["paper", "sheet", "tag", "swipe"],
      /*
       * --shadow-* (lift, lift-lg, float). This one is worse than piling up:
       * tailwind-merge falls back to `shadow-color` for a shadow-* it does not
       * recognise, so shadow-lift would be read as a *color* and shadow-none
       * could never displace it.
       */
      shadow: ["lift", "lift-lg", "float"],
      /* --ease-paper, against ease-linear/ease-out. */
      ease: ["paper"],
      /*
       * --text-fine-print, the kit's one named font size. `text-` plus any name
       * reads as a text color, so without this entry text-fine-print would
       * cancel out the text-annotation written beside it on every filing line -
       * and text-sm would never displace it.
       */
      text: ["fine-print"],
    },
    classGroups: {
      /*
       * --z-index-* tokens. The stock z group takes integers and `auto` only,
       * and has no theme scale to extend, so the names go in as a class group.
       */
      z: [{ z: ["dropdown", "sticky", "overlay", "toast"] }],
      /*
       * @utility blend-marker sets mix-blend-mode, but is spelled without the
       * `mix-` prefix, so it belongs to that group by name rather than by shape.
       */
      "mix-blend": ["blend-marker"],
      /*
       * @utility text-annotation-caps is a composite (font-family, font-size,
       * letter-spacing, text-transform), and the reason it needs its own group
       * is the collision, not the merge: `text-` + any name reads as a text
       * color, so text-annotation-caps and text-annotation would cancel each
       * other out - and they are written together all over the kit (StatusMark,
       * LabelTape, the ledger heads). An exact class group beats the color
       * validator, so both survive.
       *
       * It deliberately conflicts with nothing. It overlaps font-size with
       * text-sm and friends, but dropping the whole caps treatment to honour a
       * size change would lose three other properties with it; a caller who
       * wants a bigger label keeps both classes and sets the size in CSS order.
       */
      "text-annotation-caps": ["text-annotation-caps"],
    },
  },
});
